import { useState, useEffect } from "react";
import api, { apiErr } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { Save } from "lucide-react";

const YEARS = ["2021", "2022", "2023"];

const NumInput = ({ label, value, onChange, suffix, testid }) => (
  <div className="space-y-1.5">
    <Label className="text-sm">{label}</Label>
    <div className="relative">
      <Input data-testid={testid} className="h-10 border-[#DDE5E2] tnum pr-8" value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^\d.,]/g, ""))} />
      {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#5B6E78]">{suffix}</span>}
    </div>
  </div>
);

const toNum = (v) => {
  const n = parseFloat(String(v).replace(",", "."));
  return isNaN(n) ? 0 : n;
};

export const Parametri = () => {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/parametri").then((r) => {
      const d = r.data;
      setP({
        aliquote: { 2021: d.aliquote["2021"] * 100, 2022: d.aliquote["2022"] * 100, 2023: d.aliquote["2023"] * 100 },
        soglia_bassa: d.soglia_bassa * 100,
        soglia_alta: d.soglia_alta * 100,
        coeff_basso: d.coeff_basso * 100,
        coeff_medio: d.coeff_medio * 100,
        coeff_alto: d.coeff_alto * 100,
        massimali: { ...d.massimali },
      });
    }).catch((e) => toast.error(apiErr(e)));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        aliquote: {
          2021: toNum(p.aliquote[2021]) / 100,
          2022: toNum(p.aliquote[2022]) / 100,
          2023: toNum(p.aliquote[2023]) / 100,
        },
        soglia_bassa: toNum(p.soglia_bassa) / 100,
        soglia_alta: toNum(p.soglia_alta) / 100,
        coeff_basso: toNum(p.coeff_basso) / 100,
        coeff_medio: toNum(p.coeff_medio) / 100,
        coeff_alto: toNum(p.coeff_alto) / 100,
        massimali: {
          2021: toNum(p.massimali["2021"]),
          2022: toNum(p.massimali["2022"]),
          2023: toNum(p.massimali["2023"]),
        },
      };
      await api.put("/admin/parametri", payload);
      toast.success("Parametri salvati.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  if (!p) return <div className="py-16 text-center text-[#5B6E78]">Caricamento...</div>;

  const upAliquota = (y, v) => setP((s) => ({ ...s, aliquote: { ...s.aliquote, [y]: v } }));
  const upMass = (y, v) => setP((s) => ({ ...s, massimali: { ...s.massimali, [y]: v } }));

  return (
    <div data-testid="admin-tax-parameters-manager" className="max-w-3xl">
      <h1 className="text-2xl font-semibold text-[#2B3A42] mb-2">Parametri di calcolo</h1>
      <p className="text-sm text-[#5B6E78] mb-6">Questi parametri sono riservati e influenzano il calcolo della stima.</p>

      <div className="space-y-5">
        <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5">
          <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">Aliquote per anno</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {YEARS.map((y) => (
              <NumInput key={y} label={`Aliquota ${y}`} suffix="%" testid={`param-aliquota-${y}`}
                value={p.aliquote[y]} onChange={(v) => upAliquota(y, v)} />
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5">
          <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">Soglie quota</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumInput label="Soglia bassa" suffix="%" testid="param-soglia-bassa" value={p.soglia_bassa} onChange={(v) => setP((s) => ({ ...s, soglia_bassa: v }))} />
            <NumInput label="Soglia alta" suffix="%" testid="param-soglia-alta" value={p.soglia_alta} onChange={(v) => setP((s) => ({ ...s, soglia_alta: v }))} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5">
          <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">Coefficienti</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <NumInput label="Coeff. quota ≤ soglia bassa" suffix="%" testid="param-coeff-basso" value={p.coeff_basso} onChange={(v) => setP((s) => ({ ...s, coeff_basso: v }))} />
            <NumInput label="Coeff. quota media" suffix="%" testid="param-coeff-medio" value={p.coeff_medio} onChange={(v) => setP((s) => ({ ...s, coeff_medio: v }))} />
            <NumInput label="Coeff. quota alta" suffix="%" testid="param-coeff-alto" value={p.coeff_alto} onChange={(v) => setP((s) => ({ ...s, coeff_alto: v }))} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-[#DDE5E2] p-5">
          <h3 className="text-sm uppercase tracking-[0.12em] font-semibold text-[#6C8EAD] mb-4">Massimali annui sulla base agevolabile (€)</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {YEARS.map((y) => (
              <NumInput key={y} label={`Massimale ${y}`} suffix="€" testid={`param-massimale-${y}`}
                value={p.massimali[y]} onChange={(v) => upMass(y, v)} />
            ))}
          </div>
        </div>

        <Button data-testid="param-save-btn" onClick={save} disabled={saving}
          className="gap-2 rounded-full bg-[#9CAF88] hover:bg-[#7C8F69] text-white">
          <Save className="h-4 w-4" /> {saving ? "Salvataggio..." : "Salva parametri"}
        </Button>
      </div>
    </div>
  );
};
